import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

interface UnlockCondition {
  id: string;
  description: string;
  check: (state: SimulationState) => boolean;
}

/**
 * Hardcoded unlock conditions for the game-studio theme.
 * In a future iteration, these will be defined in theme data (unlocks.json).
 */
const UNLOCK_CONDITIONS: UnlockCondition[] = [
  // Categories unlock at company levels
  { id: 'category_simulation', description: 'Simulation genre unlocked!', check: (s) => s.company.level >= 2 },
  { id: 'category_adventure', description: 'Adventure genre unlocked!', check: (s) => s.company.level >= 3 },
  { id: 'category_puzzle', description: 'Puzzle genre unlocked!', check: (s) => s.company.level >= 3 },
  { id: 'category_strategy', description: 'Strategy genre unlocked!', check: (s) => s.company.level >= 4 },
  { id: 'category_sports', description: 'Sports genre unlocked!', check: (s) => s.company.level >= 5 },
  { id: 'category_racing', description: 'Racing genre unlocked!', check: (s) => s.company.level >= 6 },
  { id: 'category_horror', description: 'Horror genre unlocked!', check: (s) => s.company.level >= 7 },
  { id: 'category_casual', description: 'Casual genre unlocked!', check: (s) => s.company.level >= 4 },

  // Project sizes
  { id: 'size_medium', description: 'Medium projects unlocked!', check: (s) => s.company.level >= 3 },
  { id: 'size_large', description: 'Large projects unlocked!', check: (s) => s.company.level >= 6 && s.staff.length >= 8 },
  { id: 'size_aaa', description: 'AAA projects unlocked!', check: (s) => s.company.level >= 10 && s.staff.length >= 16 },

  // Staff roles
  { id: 'role_producer', description: 'Producer role unlocked!', check: (s) => s.company.level >= 4 },
  { id: 'role_qa_tester', description: 'QA Tester role unlocked!', check: (s) => s.company.totalProductsReleased >= 3 },

  // Office upgrades
  { id: 'office_2', description: 'Small Office upgrade available!', check: (s) => s.company.level >= 2 },
  { id: 'office_3', description: 'Office Suite upgrade available!', check: (s) => s.company.level >= 4 },
  { id: 'office_4', description: 'Office Floor upgrade available!', check: (s) => s.company.level >= 7 },
  { id: 'office_5', description: 'HQ Building upgrade available!', check: (s) => s.company.level >= 10 },

  // Features
  { id: 'feature_teams', description: 'Teams feature unlocked!', check: (s) => s.office.level >= 3 },
  { id: 'feature_training', description: 'Staff training unlocked!', check: (s) => s.company.level >= 2 },
];

export function unlockSystemUpdate(
  state: SimulationState,
  _themeData: ThemeData,
  _rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  for (const condition of UNLOCK_CONDITIONS) {
    if (state.unlocks.includes(condition.id)) continue;

    if (condition.check(state)) {
      state.unlocks.push(condition.id);
      events.push({
        type: 'UNLOCK_ACHIEVED',
        unlockId: condition.id,
        description: condition.description,
      });
    }
  }

  return events;
}
